import {
    ChartBarIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline';

interface QuickStatsProps {
    activeProjects: number;
    atRiskProjects: number;
    completedProjects: number;
    totalTeamMembers: number;
}

interface StatCardProps {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: number;
    variant?: 'default' | 'warning' | 'success' | 'info';
}

const StatCard = ({ icon: Icon, label, value, variant = 'default' }: StatCardProps) => {
    const variantStyles = {
        default: 'bg-blue-50 text-blue-600',
        warning: 'bg-amber-50 text-amber-800',
        success: 'bg-emerald-50 text-emerald-600',
        info: 'bg-gray-50 text-gray-600',
    };

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${variantStyles[variant]}`}>
                    <Icon className="w-6 h-6" aria-hidden="true" />
                </div>
                <div>
                    <p className="text-sm font-medium text-gray-600">{label}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
            </div>
        </div>
    );
};

/**
 * QuickStats Component
 * Displays 4 key metrics in a grid layout
 * 
 * Story: 2.1 - Dashboard/Overview
 * Wireframe: Section 1.2 - Quick Stats Bar
 */
export const QuickStats = ({
    activeProjects,
    atRiskProjects,
    completedProjects,
    totalTeamMembers,
}: QuickStatsProps) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
                icon={ChartBarIcon}
                label="Active Projects"
                value={activeProjects}
                variant="default"
            />
            <StatCard
                icon={ExclamationTriangleIcon}
                label="At Risk"
                value={atRiskProjects}
                variant="warning"
            />
            <StatCard
                icon={CheckCircleIcon}
                label="Completed"
                value={completedProjects}
                variant="success"
            />
            <StatCard
                icon={UserGroupIcon}
                label="Team Members"
                value={totalTeamMembers}
                variant="info"
            />
        </div>
    );
};

